import { cn } from '@tempo-io/sui-class-utils';
import { format, parseISO } from 'date-fns';
import { formatShortDate } from '@/components/loop-planner/common/lib/formatHeaderDateLabel';
import { DateFormat, formatLocalDate } from '@/lib/utils/common/dateFormats';

interface TooltipScheduleBarProps {
  expectedStart: string;
  expectedFinish: string;
  latestFinish?: string;
  isAtRisk: boolean;
}

export const TooltipScheduleBar = ({
  expectedStart,
  expectedFinish,
  latestFinish,
  isAtRisk,
}: TooltipScheduleBarProps) => {
  const startDate = parseISO(expectedStart);
  const finishDate = parseISO(expectedFinish);
  const latestDate = latestFinish ? parseISO(latestFinish) : finishDate;

  const totalDuration = latestDate.getTime() - startDate.getTime();
  const expectedDuration = finishDate.getTime() - startDate.getTime();
  const expectedWidthPercent = (expectedDuration / totalDuration) * 100;
  const uncertaintyWidthPercent = 100 - expectedWidthPercent;

  const formatDate = (dateStr: string) => {
    const date = parseISO(formatLocalDate(parseISO(dateStr).toISOString(), DateFormat.ISO_DATE));
    const month = format(date, DateFormat.MONTH_NUMBER);
    const day = format(date, DateFormat.DAY_NUMBER);
    return formatShortDate(month, day);
  };

  const hasUncertaintySection = latestFinish && uncertaintyWidthPercent > 0;
  const hasSolidSection = expectedWidthPercent > 0;

  return (
    <div className="relative h-7 @container/tooltip-bar">
      <div className="absolute inset-0 flex items-center">
        {hasSolidSection && (
          <div
            className="absolute h-full bg-bg-neutral-bold rounded-l-lg flex items-center @container/solid"
            style={{
              left: '0%',
              width: `${expectedWidthPercent}%`,
            }}
          >
            <span className="absolute left-2 text-xs font-bold text-text-neutral-inverted hidden @[60px]/solid:inline">
              {formatDate(expectedStart)}
            </span>
            <span className="absolute right-2 text-xs font-bold text-text-neutral-inverted hidden @[120px]/solid:inline">
              {formatDate(expectedFinish)}
            </span>
          </div>
        )}
        {hasUncertaintySection && (
          <div
            className={cn(
              'absolute h-full border border-dashed border-icon-brand-secondary bg-bg-neutral flex items-center @container/dashed',
              hasSolidSection ? 'rounded-r-lg' : 'rounded-lg',
            )}
            style={{
              left: `${expectedWidthPercent}%`,
              width: `${uncertaintyWidthPercent}%`,
            }}
          >
            <span
              className={cn(
                'absolute right-2 text-xs font-bold hidden @[40px]/dashed:inline',
                isAtRisk ? 'text-text-error' : 'text-text-brand',
              )}
            >
              {formatDate(latestFinish)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
